// Montage-service cart sync
// Keeps the Montageservice line item locked to its parent product:
//   - quantity matches the parent (1 boxspring → 1 montage; 2 → 2; etc.)
//   - removed automatically once the parent is gone
// Coupling lives in two line item properties added at PDP add-to-cart time
// (see assets/montage-service.js):
//   _montage_pair_id   shared between parent and addon
//   _is_montage_addon  marker on the addon line only
// Underscore-prefixed properties are hidden by the cart renderer.
import { CartUpdateEvent, ThemeEvents } from "@theme/events";

const MONTAGE_PAIR_KEY = "_montage_pair_id";
const MONTAGE_ADDON_KEY = "_is_montage_addon";

function getRoutes() {
  const r = (window.DiekTheme && window.DiekTheme.routes) || {};
  return {
    cart: r.cart || "/cart.js",
    cartChange: r.cart_change_url || "/cart/change.js",
  };
}

function collectSectionIds() {
  const ids = new Set();
  document.querySelectorAll("cart-items-component[data-section-id]").forEach((el) => {
    if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  document.querySelectorAll("cart-drawer-component[data-section-id]").forEach((el) => {
    if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

function readMontageProps(item) {
  let isAddon = false;
  let pairId = null;
  const props = item && item.properties;
  if (props && typeof props === "object") {
    for (const k of Object.keys(props)) {
      if (k === MONTAGE_ADDON_KEY) isAddon = true;
      if (k === MONTAGE_PAIR_KEY) pairId = props[k];
    }
  }
  return { isAddon, pairId };
}

class MontageCartSync {
  constructor() {
    this._syncing = false;
    this._pending = false;
    this._onCartUpdate = this.onCartUpdate.bind(this);
    document.addEventListener(ThemeEvents.cartUpdate, this._onCartUpdate);
  }

  async onCartUpdate(event) {
    const source = event && event.detail && event.detail.data && event.detail.data.source;
    if (source === "montage-cart-sync") return;

    if (this._syncing) {
      this._pending = true;
      return;
    }

    this._syncing = true;
    try {
      await this.syncMontageLines();
      while (this._pending) {
        this._pending = false;
        await this.syncMontageLines();
      }
    } catch (err) {
      console.error("[montage-cart-sync]", err);
    } finally {
      this._syncing = false;
    }
  }

  async syncMontageLines() {
    const routes = getRoutes();
    const cartRes = await fetch(routes.cart, { headers: { Accept: "application/json" } });
    if (!cartRes.ok) return;
    const cart = await cartRes.json();
    const items = (cart && cart.items) || [];

    const parentByPair = new Map();
    for (const item of items) {
      const { isAddon, pairId } = readMontageProps(item);
      if (!isAddon && pairId) parentByPair.set(pairId, item);
    }

    const sectionIds = collectSectionIds();
    const sectionsParam = sectionIds.length > 0 ? sectionIds.join(",") : null;
    let lastResponse = null;
    let changed = false;

    for (const item of items) {
      const { isAddon, pairId } = readMontageProps(item);
      if (!isAddon || !pairId) continue;

      const parent = parentByPair.get(pairId);
      let desiredQty;
      if (!parent) {
        desiredQty = 0;
      } else if (parent.quantity !== item.quantity) {
        desiredQty = parent.quantity;
      } else {
        continue;
      }

      const body = { id: item.key, quantity: desiredQty };
      if (sectionsParam) body.sections = sectionsParam;

      try {
        const res = await fetch(routes.cartChange, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) continue;
        lastResponse = await res.json();
        changed = true;
      } catch (err) {
        console.error("[montage-cart-sync] change failed", err);
      }
    }

    if (changed) {
      document.dispatchEvent(
        new CartUpdateEvent(lastResponse || {}, "montage-cart-sync", {
          itemCount: lastResponse && lastResponse.item_count,
          source: "montage-cart-sync",
          sections: lastResponse && lastResponse.sections,
        })
      );
    }
  }
}

if (!window.__montageCartSyncInstance) {
  window.__montageCartSyncInstance = new MontageCartSync();
}
